/**
 * Hält Kamerastrom und Vorschauelement über Bildschirmwechsel hinweg am Leben.
 *
 * <p>Hintergrund: Safari auf dem iPhone fragt bei jedem neuen `getUserMedia` erneut um Erlaubnis,
 * sobald der vorige Strom beendet wurde. Beim Abarbeiten eines Schranks wechselt die Anwendung
 * ständig zwischen Aufnahme und Bestätigung — der Anwender bekäme die Rückfrage vor jedem Fach.
 *
 * <p>Deshalb gehören sowohl der Strom als auch das `<video>`-Element diesem Modul. Das Element
 * bleibt dauerhaft im Dokument und spielt durchgehend; die Bildschirme hängen es nur in ihren
 * eigenen Rahmen um. Drei Fallstricke sind damit umgangen:
 *
 * <ul>
 *   <li>Ein Strom, der einem zweiten `<video>` zugewiesen wird, liefert auf iOS oft nur noch
 *       Schwarzbild. Es gibt hier nur ein einziges Element, also keine Neuzuweisung.
 *   <li>`spur.enabled = false` liefert ebenfalls Schwarzbild. Die Spuren werden deshalb nie
 *       abgeschaltet, sondern nach Ablauf der Nachlauffrist ganz beendet. Preis: die
 *       Kameraanzeige des Geräts bleibt währenddessen an — dafür ist das Bild verlässlich da.
 *   <li>Zwei überlappende Anforderungen (React im Strict Mode montiert jede Komponente doppelt)
 *       würden zwei Kameraströme öffnen. Eine laufende Anforderung wird deshalb geteilt.
 * </ul>
 *
 * <p>Zählregel für Aufrufer: Auf jedes `anfordern()` kommt genau ein `freigeben()`, und zwar im
 * Aufräumschritt des Effekts — auch dann, wenn die Komponente vor dem Eintreffen des Stroms
 * schon wieder verschwunden ist. Wird doppelt freigegeben, hält sich das Modul für unbenutzt und
 * beendet die Kamera unter einem noch sichtbaren Sucher.
 */

/** Wie lange der Strom nach dem letzten Nutzer weiterläuft. */
const NACHLAUF_MS = 45_000

/** Frist, nach der ein Sucher ohne Bild als hängend gilt und der Strom neu geholt wird. */
const WACHHUND_MS = 1_500

const VORGABEN: MediaStreamConstraints = {
  video: { facingMode: { ideal: 'environment' }, width: { ideal: 2560 }, height: { ideal: 1920 } },
  audio: false,
}

let strom: MediaStream | null = null
let anfrage: Promise<HTMLVideoElement> | null = null
let element: HTMLVideoElement | null = null
let parkplatz: HTMLElement | null = null
let nutzer = 0
let abschaltUhr: number | null = null
let blitzAn = false

/**
 * Der Blitz überdauert den Bildschirmwechsel wie der Strom selbst - beim Abarbeiten dunkler
 * Kellerlager ist genau das gewollt. Damit ihn auch die Kopfzeile ausschalten kann, liegt sein
 * Zustand hier und wird über `useSyncExternalStore` abonniert.
 */
const blitzHorcher = new Set<() => void>()

function blitzMelden(neu: boolean) {
  if (blitzAn === neu) {
    return
  }
  blitzAn = neu
  blitzHorcher.forEach((horcher) => horcher())
}

function lebendig(pruefling: MediaStream | null): pruefling is MediaStream {
  return pruefling !== null && pruefling.getVideoTracks().some((spur) => spur.readyState === 'live')
}

/**
 * Abstellplatz für das Vorschauelement zwischen zwei Bildschirmen. Bewusst nicht `display: none`:
 * ausgeblendete Videoelemente halten iOS an, und genau das soll vermieden werden.
 */
function parkplatzHolen(): HTMLElement {
  if (!parkplatz) {
    parkplatz = document.createElement('div')
    parkplatz.setAttribute('aria-hidden', 'true')
    parkplatz.style.cssText =
      'position:fixed;left:-9999px;top:0;width:1px;height:1px;overflow:hidden;pointer-events:none;'
    document.body.appendChild(parkplatz)
  }
  return parkplatz
}

function elementHolen(): HTMLVideoElement {
  if (!element) {
    element = document.createElement('video')
    element.autoplay = true
    element.muted = true
    element.playsInline = true
    // iOS wertet das Attribut aus, nicht nur die Eigenschaft.
    element.setAttribute('playsinline', '')
    element.setAttribute('muted', '')
    parkplatzHolen().appendChild(element)
  }
  return element
}

function abspielen(video: HTMLVideoElement) {
  // Stummes Inline-Video darf ohne Geste starten; ein abgelehntes Versprechen ist trotzdem kein
  // Grund, den Bildschirm scheitern zu lassen.
  void video.play().catch(() => undefined)
}

/**
 * Meldet einen Nutzer an und liefert das gemeinsame Vorschauelement. Der Aufrufer hängt es in
 * seinen Rahmen; abgemeldet wird ausschließlich über `freigeben()`.
 */
export function anfordern(): Promise<HTMLVideoElement> {
  if (abschaltUhr !== null) {
    window.clearTimeout(abschaltUhr)
    abschaltUhr = null
  }
  nutzer += 1

  const video = elementHolen()

  if (lebendig(strom)) {
    abspielen(video)
    return Promise.resolve(video)
  }

  // Läuft bereits eine Anforderung, wird sie geteilt - sonst öffnet der Strict Mode zwei Kameras.
  if (!anfrage) {
    anfrage = navigator.mediaDevices
      .getUserMedia(VORGABEN)
      .then((geliefert) => {
        strom = geliefert
        video.srcObject = geliefert
        abspielen(video)
        void zoomZuruecksetzen(geliefert)
        return video
      })
      .finally(() => {
        anfrage = null
      })
  }
  return anfrage
}

/** Meldet einen Nutzer ab. Der Strom bleibt für die Nachlauffrist bestehen. */
export function freigeben(): void {
  nutzer = Math.max(0, nutzer - 1)
  if (element) {
    // Zurück auf den Abstellplatz, damit das Element im Dokument bleibt und weiterspielt.
    parkplatzHolen().appendChild(element)
  }
  if (nutzer > 0) {
    return
  }
  abschaltUhr = window.setTimeout(beenden, NACHLAUF_MS)
}

/** Beendet den Strom endgültig; die nächste Anforderung fragt wieder um Erlaubnis. */
export function beenden(): void {
  if (abschaltUhr !== null) {
    window.clearTimeout(abschaltUhr)
    abschaltUhr = null
  }
  strom?.getTracks().forEach((spur) => spur.stop())
  strom = null
  if (element) {
    element.srcObject = null
  }
  // Mit der Spur erlischt auch die Leuchte; die Anzeige darf das nicht verschlafen.
  blitzMelden(false)
}

/**
 * Notausgang für einen Sucher, der trotz laufender Anforderung schwarz bleibt. Der Strom wird
 * verworfen und neu geholt; die Zählung der Nutzer bleibt dabei unberührt.
 */
export async function neustarten(): Promise<HTMLVideoElement> {
  strom?.getTracks().forEach((spur) => spur.stop())
  strom = null
  anfrage = null
  blitzMelden(false)

  const video = elementHolen()
  const geliefert = await navigator.mediaDevices.getUserMedia(VORGABEN)
  strom = geliefert
  video.srcObject = geliefert
  abspielen(video)
  void zoomZuruecksetzen(geliefert)
  return video
}

/**
 * Setzt die Zoomstufe auf 1, sofern das Gerät sie über die Spur anbietet. Neuere iPhones mit
 * mehreren Rückkameras öffnen den Strom gelegentlich mit einer anderen Stufe; gemeldet war ein
 * Sucher, der von Anfang an hineingezoomt war und erst nach einem Neustart normal lief.
 *
 * <p>Bewusst nicht als Vorgabe in `getUserMedia`: Chrome fragt bei einer Zoom-Vorgabe zusätzlich
 * um die Erlaubnis für Schwenken und Zoomen.
 */
async function zoomZuruecksetzen(geliefert: MediaStream): Promise<void> {
  const spur = geliefert.getVideoTracks()[0]
  if (!spur?.getCapabilities) {
    return
  }
  const zoom = (spur.getCapabilities() as MediaTrackCapabilities & { zoom?: { min: number; max: number } })
    .zoom
  const aktuell = (spur.getSettings() as MediaTrackSettings & { zoom?: number }).zoom
  if (!zoom || aktuell === undefined || aktuell === 1 || zoom.min > 1 || zoom.max < 1) {
    return
  }
  try {
    await spur.applyConstraints({ advanced: [{ zoom: 1 } as MediaTrackConstraintSet] })
  } catch {
    // Kein Grund, den Sucher scheitern zu lassen - dann bleibt es bei der gelieferten Stufe.
  }
}

/** Wahr, wenn der Sucher tatsächlich Bilder liefert. */
export function liefertBild(): boolean {
  const spur = strom?.getVideoTracks()[0]
  return Boolean(spur && spur.readyState === 'live' && !spur.muted) && (element?.videoWidth ?? 0) > 0
}

export const wachhundFrist = WACHHUND_MS

/** Taschenlampe, sofern das Gerät sie über die Spur anbietet. */
export async function blitzSetzen(an: boolean): Promise<boolean> {
  const spur = strom?.getVideoTracks()[0]
  if (!spur) {
    return false
  }
  try {
    await spur.applyConstraints({ advanced: [{ torch: an } as MediaTrackConstraintSet] })
    blitzMelden(an)
    return true
  } catch {
    return false
  }
}

/** Abonnement für `useSyncExternalStore`; liefert die Abmeldung zurück. */
export function blitzAbonnieren(horcher: () => void): () => void {
  blitzHorcher.add(horcher)
  return () => {
    blitzHorcher.delete(horcher)
  }
}

export function blitzZustand(): boolean {
  return blitzAn
}

/** Schaltet die Leuchte aus, ohne dass ein Sucher sichtbar sein muss (Kopfzeile). */
export async function blitzAusschalten(): Promise<void> {
  if (!(await blitzSetzen(false))) {
    // Ohne Spur gibt es nichts mehr zu löschen - der Zustand wird trotzdem bereinigt.
    blitzMelden(false)
  }
}

export function blitzMoeglich(): boolean {
  const spur = strom?.getVideoTracks()[0]
  if (!spur) {
    return false
  }
  const faehigkeiten = spur.getCapabilities() as MediaTrackCapabilities & { torch?: boolean }
  return Boolean(faehigkeiten.torch)
}
